import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import axios from 'axios';

import { Button, Modal } from '@opendate-app/setlist';

import { toastError, toastInfo, toastSuccess } from '../../../shared/toastHelper';

const seriesPricesUrl = (teamId, ticketType) => `/teams/${teamId}/ticket_types/${ticketType.id}/series_prices`;

export const appliedMessage = ({ applied_count: applied, skipped, queued_count: queued }) => {
  if (queued) return `Applying to ${queued} shows in the background. We'll email you when it's done, with any shows that were skipped.`;
  const done = `Applied to ${applied} ${applied === 1 ? 'show' : 'shows'}.`;
  if (skipped.length === 0) return done;
  return `${done} Skipped ${skipped.length}: ${skipped.map((skip) => `${skip.name}, ${skip.date} (${skip.reason})`).join('; ')}`;
};

const SeriesPricesButton = ({ ticketType, teamId, unsaved }) => {
  const [showCount, setShowCount] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    let current = true;
    setShowCount(0);
    axios.get(seriesPricesUrl(teamId, ticketType))
      .then(({ data }) => current && setShowCount(data.show_count))
      .catch(() => current && setShowCount(0));
    return () => { current = false; };
  }, [teamId, ticketType.id]);

  if (showCount === 0) return null;
  if (unsaved) return <p className="ticket-drawer-muted-text">Save your price changes, then reopen this section to apply them to the rest of the series.</p>;

  const apply = () => {
    setApplying(true);
    axios.post(seriesPricesUrl(teamId, ticketType), { authenticity_token: document.querySelector('meta[name="csrf-token"]')?.content })
      .then(({ data }) => (data.queued_count || data.skipped.length ? toastInfo(appliedMessage(data)) : toastSuccess(appliedMessage(data))))
      .catch(() => toastError("Something went wrong applying the prices. Some shows may have been updated; check them before trying again."))
      .finally(() => {
        setApplying(false);
        setConfirming(false);
      });
  };

  const shows = `${showCount} other upcoming ${showCount === 1 ? 'show' : 'shows'}`;
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
        {`Apply prices to ${showCount} other ${showCount === 1 ? 'show' : 'shows'}`}
      </Button>
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={`Apply to ${shows}?`}
        footer={(
          <>
            <Button variant="secondary" disabled={applying} onClick={() => setConfirming(false)}>Cancel</Button>
            <Button variant="primary" disabled={applying} onClick={apply}>{`Apply to ${showCount}`}</Button>
          </>
        )}
      >
        <p>
          {`This sets the Adult price and the other prices (like Child) of ${ticketType.base_name.replace(/ - Adult$/, "")} on ${shows} to the prices saved here. Tickets already sold keep the price fans paid. `}
          {`Shows without ${ticketType.base_name}, or where a price being removed already has sales, are skipped and listed. `}
          Past, canceled and sold-out shows are never changed.
        </p>
      </Modal>
    </>
  );
};

SeriesPricesButton.propTypes = {
  ticketType: PropTypes.shape({
    id: PropTypes.number.isRequired,
    base_name: PropTypes.string,
  }).isRequired,
  teamId: PropTypes.number.isRequired,
  unsaved: PropTypes.bool,
};

SeriesPricesButton.defaultProps = {
  unsaved: false,
};

export default SeriesPricesButton;
